import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Users, Lock, FileText, Mail, MapPin, Phone, ExternalLink, Baby, RefreshCw } from 'lucide-react';
import { ANIMATION_VARIANTS, HERO_CONTENT, COMPANY_INFO } from '../constants';
import { 
  useFloatingParticles,
  usePerformanceMonitoring,
  useOptimizedBackground
} from '../hooks/useAnimations';
import { GlassCard, StyledIcon } from '../components/animations';

const PrivacyPolicyPage: React.FC = () => {
  usePerformanceMonitoring('PrivacyPolicyPage');
  
  // Global floating particles for the entire page (mandatory pattern)
  const { particlesRef, containerClasses: particlesContainerClasses, particles } = useFloatingParticles(80);

  // Optimized background for alternating sections
  const { backgroundClasses: heroBackgroundClasses, backgroundStyle: heroBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: contentBackgroundClasses, backgroundStyle: contentBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: additionalBackgroundClasses, backgroundStyle: additionalBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const sections = [
    {
      id: 'information-collect',
      title: '1. Information We Collect',
      icon: Eye,
      content: [
        {
          subtitle: 'Personal Information',
          text: 'When you voluntarily provide it, we may collect personal information such as your name, email address, phone number, billing information, or other contact details when you sign up for newsletters, request services, or contact us through the Site.'
        },
        {
          subtitle: 'Usage Information',
          text: 'We automatically collect data related to your use of our website, such as your IP address, browser type, operating system, pages viewed, and the dates and times of your visits. This information is collected through cookies and similar technologies.'
        },
        {
          subtitle: 'Cookies and Tracking Technologies',
          text: 'Our website may use cookies and other tracking technologies to improve your experience, analyse usage, and deliver personalized content. You can manage your cookie preferences through your browser settings.'
        }
      ]
    },
    {
      id: 'how-we-use',
      title: '2. How We Use Your Information',
      icon: Users,
      content: [
        {
          subtitle: 'To Provide Services',
          text: 'Personal information is used to provide and deliver the services you request, such as responding to inquiries, processing transactions, and offering customer support.'
        },
        {
          subtitle: 'To Improve Our Website',
          text: 'Usage information helps us understand how visitors interact with our website, allowing us to enhance functionality, improve performance, and deliver a better user experience.'
        },
        {
          subtitle: 'Marketing and Communications',
          text: 'With your consent, we may use your contact information to send you newsletters, promotions, or updates about our services. You can opt out of receiving these communications at any time.'
        },
        {
          subtitle: 'Legal and Security Purposes',
          text: 'We may use your information to comply with legal obligations, enforce our terms of service, protect our rights and property, or detect and prevent fraud or security issues.'
        }
      ]
    },
    {
      id: 'how-we-share',
      title: '3. How We Share Your Information',
      icon: Shield,
      content: [
        {
          subtitle: 'Service Providers',
          text: 'We may share your personal information with trusted third-party service providers who assist us in operating our business, such as payment processors, IT service providers, or marketing agencies. These service providers are bound by contractual obligations to protect your information.'
        },
        {
          subtitle: 'Legal Requirements',
          text: 'We may disclose personal information if required by law or in response to valid legal requests, such as a subpoena or court order.'
        },
        {
          subtitle: 'Business Transfers',
          text: 'In the event of a merger, acquisition, or sale of our business, your personal information may be transferred as part of the transaction, subject to the same privacy protections.'
        }
      ]
    },
    {
      id: 'data-security',
      title: '4. Data Security',
      icon: Lock,
      content: [
        {
          subtitle: 'Security Measures',
          text: 'We implement appropriate technical and organizational measures to safeguard your personal information from unauthorized access, disclosure, or misuse. However, please note that no method of transmission over the Internet or electronic storage is completely secure, and we cannot guarantee absolute security.'
        }
      ]
    },
    {
      id: 'your-rights',
      title: '5. Your Rights and Choices',
      icon: FileText,
      content: [
        {
          subtitle: 'Access and Correction',
          text: 'You can request access to the personal information we hold about you and request that any inaccuracies be corrected.'
        },
        {
          subtitle: 'Data Deletion',
          text: 'You have the right to request that we delete your personal information, subject to certain legal or contractual obligations.'
        },
        {
          subtitle: 'Opt-Out of Marketing',
          text: 'You can opt out of receiving marketing communications from us at any time by following the unsubscribe instructions in our emails or contacting us directly.'
        },
        {
          subtitle: 'Cookie Preferences',
          text: 'You can manage or disable cookies through your browser settings. However, disabling cookies may affect your ability to use certain features of our website.'
        }
      ]
    },
    {
      id: 'third-party-websites',
      title: '6. Links to Third-Party Websites',
      icon: ExternalLink,
      content: [
        {
          subtitle: 'External Links',
          text: 'Our website may contain links to third-party websites that are not operated by us. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party websites you visit.'
        }
      ]
    },
    {
      id: 'children-privacy',
      title: '7. Children\'s Privacy',
      icon: Baby,
      content: [
        {
          subtitle: 'Age Restrictions',
          text: 'Our services are not directed at children under the age of 13, and we do not knowingly collect personal information from children. If we become aware that we have inadvertently collected information from a child, we will take steps to delete it.'
        }
      ]
    },
    {
      id: 'policy-changes',
      title: '8. Changes to This Privacy Policy',
      icon: RefreshCw,
      content: [
        {
          subtitle: 'Policy Updates',
          text: 'We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. Any changes will be posted on this page with the "Effective Date" updated accordingly. We encourage you to review this policy periodically to stay informed about how we are protecting your information.'
        }
      ]
    }
  ];

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={ANIMATION_VARIANTS.fadeIn}
      className="min-h-screen relative"
    >
      {/* Global Floating Particles Background */}
      <div ref={particlesRef} className={`${particlesContainerClasses} fixed inset-0 z-30`}>
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={particle.classes}
            style={particle.style}
          />
        ))}
      </div>

      {/* Page Content */}
      <div className="relative z-40">
        {/* Hero Section */}
        <section className="section-padding relative text-white">
          {/* Hero Background - Normal orientation */}
          <div 
            className={heroBackgroundClasses}
            style={heroBackgroundStyle}
          />
          
          <div className="container relative z-40 pt-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                variants={ANIMATION_VARIANTS.slideUp}
                className="flex items-center justify-center mb-6"
              >
                <StyledIcon 
                  icon={Shield} 
                  size="xl" 
                  color="primary" 
                  variant="static" 
                />
              </motion.div>
              
              <motion.h1
                variants={ANIMATION_VARIANTS.slideUp}
                className="heading-1 text-white-gradient mb-6"
              >
                Privacy Policy
              </motion.h1>
              
              <motion.p
                variants={ANIMATION_VARIANTS.slideUp}
                className="hero-subtitle max-w-2xl mx-auto mb-8"
              >
                Your privacy is important to us. <br></br>This Privacy Policy explains how M Telecommunication Sdn Bhd collects, uses, shares, and protects your information.
              </motion.p>

              <motion.div
                variants={ANIMATION_VARIANTS.slideUp}
                className="text-sm text-gray-400"
              >
                Last Updated: August 5, 2025
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="relative">
          {/* Content Background - Rotated orientation for alternating effect */}
          <div 
            className={contentBackgroundClasses}
            style={{
              ...contentBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            {/* Introduction */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.fadeIn}
              className="max-w-4xl mx-auto mb-8"
            >
              <GlassCard className="rounded-2xl p-8 text-center">
                <p className="body-text text-gray-300">
                  This Privacy Policy explains how M Telecommunication Sdn Bhd ("we," "us," or "our") collects, uses, shares, and protects information obtained from visitors to our website ("Site") and customers who engage with our services. We are committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner.
                </p>
              </GlassCard>
            </motion.div>

            {/* Privacy Policy Sections */}
            <div className="space-y-8">
              {sections.map((section) => (
                <motion.div
                  key={section.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={ANIMATION_VARIANTS.staggerChildren}
                  className="max-w-4xl mx-auto"
                >
                  <GlassCard className="rounded-2xl p-8">
                    <motion.div
                      variants={ANIMATION_VARIANTS.slideUp}
                      className="flex items-center mb-6"
                    >
                      <div className="mr-4">
                        <StyledIcon 
                          icon={section.icon}
                          size="sm"
                          color="primary"
                          variant="default"
                        />
                      </div>
                      <h2 className="heading-3 text-white-gradient">
                        {section.title}
                      </h2>
                    </motion.div>

                    <div className="space-y-6">
                      {section.content.map((item, itemIndex) => (
                        <motion.div
                          key={itemIndex}
                          variants={ANIMATION_VARIANTS.slideUp}
                          className="border-l-2 border-primary-400/30 pl-6"
                        >
                          <h3 className="text-lg font-semibold text-white mb-3">
                            {item.subtitle}
                          </h3>
                          <p className="body-text text-gray-300 leading-relaxed">
                            {item.text}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  </GlassCard>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Us Section */}
        <section className="relative py-16">
          {/* Contact Background - Rotated orientation */}
          <div 
            className={additionalBackgroundClasses}
            style={{
              ...additionalBackgroundStyle,
              backgroundPosition: 'bottom',
              transform: 'translateZ(0)',
            }}
          />
          
          <div className="container relative z-40">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
              className="max-w-4xl mx-auto"
            >
              <GlassCard className="rounded-2xl p-8">
                <motion.div
                  variants={ANIMATION_VARIANTS.slideUp}
                  className="flex items-center mb-6"
                >
                  <div className="mr-4">
                    <StyledIcon 
                      icon={Mail}
                      size="sm"
                      color="primary"
                      variant="default"
                    />
                  </div>
                  <h2 className="heading-3 text-white-gradient">
                    9. Contact Us
                  </h2>
                </motion.div>

                <motion.div
                  variants={ANIMATION_VARIANTS.slideUp}
                  className="border-l-2 border-primary-400/30 pl-6 mb-8"
                >
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Get in Touch
                  </h3>
                  <p className="body-text text-gray-300 leading-relaxed mb-6">
                    If you have any questions about this Privacy Policy or how we handle your personal information, please contact us using the information provided below.
                  </p>
                </motion.div>

                <motion.div
                  variants={ANIMATION_VARIANTS.slideUp}
                  className="bg-primary-500/10 rounded-lg p-6 border border-primary-400/20"
                >
                  <div className="text-center space-y-4">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      {COMPANY_INFO.fullName}
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex items-center justify-center md:justify-start space-x-3">
                          <StyledIcon 
                            icon={MapPin}
                            size="xs"
                            color="primary"
                            variant="default"
                          />
                          <div className="text-gray-300 text-sm text-left">
                            <p>{COMPANY_INFO.address.street}</p>
                            <p>{COMPANY_INFO.address.postcode} {COMPANY_INFO.address.city},</p>
                            <p>{COMPANY_INFO.address.state} {COMPANY_INFO.address.country}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-center md:justify-start space-x-3">
                          <StyledIcon 
                            icon={Mail}
                            size="xs"
                            color="primary"
                            variant="default"
                          />
                          <a 
                            href={`mailto:${COMPANY_INFO.contact.email}`}
                            className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm"
                          >
                            {COMPANY_INFO.contact.email}
                          </a>
                        </div>
                        
                        <div className="flex items-center justify-center md:justify-start space-x-3">
                          <StyledIcon 
                            icon={Phone}
                            size="xs"
                            color="primary"
                            variant="default"
                          />
                          <a 
                            href={`tel:${COMPANY_INFO.contact.phone}`}
                            className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm"
                          >
                            {COMPANY_INFO.contact.phone}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.p
                  variants={ANIMATION_VARIANTS.slideUp}
                  className="body-text text-gray-400 text-center mt-8 text-sm"
                >
                  By using our website or services, you consent to the terms outlined in this Privacy Policy.
                </motion.p>
              </GlassCard>
            </motion.div>
          </div>
        </section>
      </div>
    </motion.div>
  );
};

export default PrivacyPolicyPage;
